import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Save, X, Award, Trophy, Play, User, Clock, Target, Zap } from 'lucide-react';
import { toast } from 'react-hot-toast';
import certificateAPI from '../services/certificateAPI';

const PrizeManagement = () => {
  const [prizes, setPrizes] = useState([]);
  const [editingPrize, setEditingPrize] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showTestSection, setShowTestSection] = useState(false);
  const [testResults, setTestResults] = useState(null);
  const [testUser, setTestUser] = useState({
    email: 'test@example.com',
    huntsCompleted: 0,
    fastestTime: null,
    perfectCompletions: 0,
    nightCompletions: 0,
    weekendCompletions: 0,
    lastCompletionTime: null
  });
  const [newPrize, setNewPrize] = useState({
    id: '',
    name: '',
    description: '',
    icon: '🏆',
    rarity: 'common',
    enabled: true,
    condition: 'hunts_completed',
    threshold: 1
  });

  const rarities = ['common', 'uncommon', 'rare', 'epic', 'legendary'];
  const icons = ['🏆', '⚡', '💎', '🦉', '🗡️', '🐦', '🔥', '👑', '⭐', '🎯', '🚀', '💫'];
  
  const presetConditions = [
    { value: 'hunts_completed', label: 'Hunts Completed', description: 'Total number of hunts completed' },
    { value: 'completion_time', label: 'Completion Time', description: 'Time taken to complete hunt (minutes)' },
    { value: 'perfect_score', label: 'Perfect Score', description: 'Solved all puzzles without hints' },
    { value: 'night_completion', label: 'Night Completion', description: 'Completed hunt after 8 PM' },
    { value: 'weekend_completion', label: 'Weekend Completion', description: 'Completed hunt on weekend' },
    { value: 'early_completion', label: 'Early Completion', description: 'Completed hunt before 10 AM' },
    { value: 'streak_count', label: 'Streak Count', description: 'Consecutive hunts completed' },
    { value: 'total_perfect', label: 'Total Perfect', description: 'Total perfect completions' }
  ];

  useEffect(() => {
    loadPrizes();
  }, []);

  const loadPrizes = async () => {
    // Always load preset prizes - these are the prize definitions, not awarded prizes
    // The Prize Management page is for managing prize types/rules, not viewing awarded prizes
    loadPresetPrizes();
  };

  const loadPresetPrizes = () => {
    // Preset prizes with conditions
    const presetPrizes = [
      {
        id: 'first_completion',
        name: 'Explorer Badge',
        description: 'First hunt completion',
        icon: '🏆',
        rarity: 'common',
        enabled: true,
        condition: 'hunts_completed',
        threshold: 1
      },
      {
        id: 'speed_demon',
        name: 'Speed Demon',
        description: 'Completed hunt in under 1 hour',
        icon: '⚡',
        rarity: 'rare',
        enabled: true,
        condition: 'completion_time',
        threshold: 3600 // seconds (1 hour)
      },
      {
        id: 'perfect_score',
        name: 'Perfectionist',
        description: 'Solved all puzzles without hints',
        icon: '💎',
        rarity: 'epic',
        enabled: true,
        condition: 'perfect_score',
        threshold: 1
      },
      {
        id: 'night_owl',
        name: 'Night Owl',
        description: 'Completed hunt after 8 PM',
        icon: '🦉',
        rarity: 'uncommon',
        enabled: true,
        condition: 'night_completion',
        threshold: 1
      },
      {
        id: 'weekend_warrior',
        name: 'Weekend Warrior',
        description: 'Completed hunt on weekend',
        icon: '🗡️',
        rarity: 'common',
        enabled: true,
        condition: 'weekend_completion',
        threshold: 1
      },
      {
        id: 'early_bird',
        name: 'Early Bird',
        description: 'Completed hunt before 10 AM',
        icon: '🐦',
        rarity: 'uncommon',
        enabled: true,
        condition: 'early_completion',
        threshold: 1
      },
      {
        id: 'streak_master',
        name: 'Streak Master',
        description: 'Completed 3 hunts in a row',
        icon: '🔥',
        rarity: 'epic',
        enabled: true,
        condition: 'streak_count',
        threshold: 3
      },
      {
        id: 'explorer_legend',
        name: 'Explorer Legend',
        description: 'Completed 10 hunts total',
        icon: '👑',
        rarity: 'legendary',
        enabled: true,
        condition: 'hunts_completed',
        threshold: 10
      }
    ];
    setPrizes(presetPrizes);
  };

  const handleAddPrize = () => {
    if (!newPrize.id || !newPrize.name) {
      toast.error('Please fill in all required fields');
      return;
    }

    setPrizes([...prizes, { ...newPrize }]);
    setNewPrize({
      id: '',
      name: '',
      description: '',
      icon: '🏆',
      rarity: 'common',
      enabled: true
    });
    setShowAddForm(false);
    toast.success('Prize added successfully!');
  };

  const handleEditPrize = (prize) => {
    setEditingPrize({ ...prize });
  };

  const handleSaveEdit = () => {
    setPrizes(prizes.map(p => p.id === editingPrize.id ? editingPrize : p));
    setEditingPrize(null);
    toast.success('Prize updated successfully!');
  };

  const handleDeletePrize = (prizeId) => {
    if (window.confirm('Are you sure you want to delete this prize?')) {
      setPrizes(prizes.filter(p => p.id !== prizeId));
      toast.success('Prize deleted successfully!');
    }
  };

  const togglePrizeEnabled = (prizeId) => {
    setPrizes(prizes.map(p => 
      p.id === prizeId ? { ...p, enabled: !p.enabled } : p
    ));
  };

  const testPrizes = () => {
    const results = [];
    
    prizes.forEach(prize => {
      if (!prize.enabled) return;
      
      let earned = false;
      let reason = '';
      
      switch (prize.condition) {
        case 'hunts_completed':
          earned = testUser.huntsCompleted >= prize.threshold;
          reason = `Hunts completed: ${testUser.huntsCompleted}/${prize.threshold}`;
          break;
        case 'completion_time':
          // Prize threshold is in seconds, testUser.fastestTime is in minutes
          const thresholdMinutes = Math.floor(prize.threshold / 60);
          const testTimeSeconds = testUser.fastestTime ? testUser.fastestTime * 60 : null;
          earned = testTimeSeconds && testTimeSeconds <= prize.threshold;
          reason = `Fastest time: ${testUser.fastestTime || 'N/A'} minutes (≤${thresholdMinutes} minutes)`;
          break;
        case 'perfect_score':
          earned = testUser.perfectCompletions >= prize.threshold;
          reason = `Perfect completions: ${testUser.perfectCompletions}/${prize.threshold}`;
          break;
        case 'night_completion':
          earned = testUser.nightCompletions >= prize.threshold;
          reason = `Night completions: ${testUser.nightCompletions}/${prize.threshold}`;
          break;
        case 'weekend_completion':
          earned = testUser.weekendCompletions >= prize.threshold;
          reason = `Weekend completions: ${testUser.weekendCompletions}/${prize.threshold}`;
          break;
        case 'early_completion':
          earned = testUser.lastCompletionTime && new Date(testUser.lastCompletionTime).getHours() < 10;
          reason = `Last completion time: ${testUser.lastCompletionTime ? new Date(testUser.lastCompletionTime).toLocaleTimeString() : 'N/A'}`;
          break;
        case 'streak_count':
          earned = testUser.huntsCompleted >= prize.threshold;
          reason = `Streak count: ${testUser.huntsCompleted}/${prize.threshold}`;
          break;
        case 'total_perfect':
          earned = testUser.perfectCompletions >= prize.threshold;
          reason = `Total perfect: ${testUser.perfectCompletions}/${prize.threshold}`;
          break;
      }
      
      results.push({
        prize,
        earned,
        reason
      });
    });
    
    setTestResults(results);
    toast.success('Prize test completed!');
  };

  const getRarityColor = (rarity) => {
    const colors = {
      common: 'bg-green-100 text-green-800',
      uncommon: 'bg-blue-100 text-blue-800',
      rare: 'bg-yellow-100 text-yellow-800',
      epic: 'bg-purple-100 text-purple-800',
      legendary: 'bg-orange-100 text-orange-800'
    };
    return colors[rarity] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="header-gradient rounded-lg p-6 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">🎁 Prize Management</h1>
            <p className="mt-2 text-lg">
              Add, edit, and manage virtual prizes
            </p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={loadPrizes}
              className="btn-secondary-gradient flex items-center space-x-2"
            >
              <Target className="h-5 w-5" />
              <span>Refresh</span>
            </button>
            <button
              onClick={() => setShowTestSection(!showTestSection)}
              className="btn-warning-gradient flex items-center space-x-2"
            >
              <Play className="h-5 w-5" />
              <span>Test Prizes</span>
            </button>
            <button
              onClick={() => setShowAddForm(true)}
              className="btn-gradient flex items-center space-x-2"
            >
              <Plus className="h-5 w-5" />
              <span>Add Prize</span>
            </button>
          </div>
        </div>
      </div>

      {/* Test Section */}
      {showTestSection && (
        <div className="email-card">
          <div className="email-card-header">
            <h3 className="text-lg font-semibold">🧪 Test Prize System</h3>
            <p className="text-sm">Configure a test user and see which prizes they would earn</p>
          </div>
          <div className="email-card-body">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Test User Email
                </label>
                <input
                  type="email"
                  value={testUser.email}
                  onChange={(e) => setTestUser({...testUser, email: e.target.value})}
                  className="input-gradient w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Hunts Completed
                </label>
                <input
                  type="number"
                  value={testUser.huntsCompleted}
                  onChange={(e) => setTestUser({...testUser, huntsCompleted: parseInt(e.target.value) || 0})}
                  className="input-gradient w-full"
                  min="0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fastest Time (minutes)
                </label>
                <input
                  type="number"
                  value={testUser.fastestTime || ''}
                  onChange={(e) => setTestUser({...testUser, fastestTime: parseInt(e.target.value) || null})}
                  className="input-gradient w-full"
                  min="0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Perfect Completions
                </label>
                <input
                  type="number"
                  value={testUser.perfectCompletions}
                  onChange={(e) => setTestUser({...testUser, perfectCompletions: parseInt(e.target.value) || 0})}
                  className="input-gradient w-full"
                  min="0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Night Completions
                </label>
                <input
                  type="number"
                  value={testUser.nightCompletions}
                  onChange={(e) => setTestUser({...testUser, nightCompletions: parseInt(e.target.value) || 0})}
                  className="input-gradient w-full"
                  min="0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Weekend Completions
                </label>
                <input
                  type="number"
                  value={testUser.weekendCompletions}
                  onChange={(e) => setTestUser({...testUser, weekendCompletions: parseInt(e.target.value) || 0})}
                  className="input-gradient w-full"
                  min="0"
                />
              </div>
              <div className="md:col-span-2 lg:col-span-3">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Last Completion Time
                </label>
                <input
                  type="datetime-local"
                  value={testUser.lastCompletionTime ? new Date(testUser.lastCompletionTime).toISOString().slice(0, 16) : ''}
                  onChange={(e) => setTestUser({...testUser, lastCompletionTime: e.target.value ? new Date(e.target.value).toISOString() : null})}
                  className="input-gradient w-full"
                />
              </div>
            </div>
            
            <div className="flex justify-center">
              <button
                onClick={testPrizes}
                className="btn-success-gradient flex items-center space-x-2"
              >
                <Play className="h-5 w-5" />
                <span>Test Prizes</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Test Results */}
      {testResults && (
        <div className="email-card">
          <div className="email-card-header">
            <h3 className="text-lg font-semibold">📊 Test Results</h3>
            <p className="text-sm">Prizes that would be earned by {testUser.email}</p>
          </div>
          <div className="email-card-body">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {testResults.map((result, index) => (
                <div
                  key={index}
                  className={`p-4 rounded-lg border-2 ${
                    result.earned 
                      ? 'border-green-500 bg-green-50' 
                      : 'border-gray-300 bg-gray-50'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <span className="text-2xl">{result.prize.icon}</span>
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900">{result.prize.name}</h4>
                      <p className="text-sm text-gray-600">{result.prize.description}</p>
                      <p className="text-xs text-gray-500 mt-1">{result.reason}</p>
                    </div>
                    <div className="text-right">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        result.earned ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {result.earned ? '✅ Earned' : '❌ Not Earned'}
                      </span>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ml-2 ${getRarityColor(result.prize.rarity)}`}>
                        {result.prize.rarity}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Add Prize Form */}
      {showAddForm && (
        <div className="email-card">
          <div className="email-card-header">
            <h3 className="text-lg font-semibold">Add New Prize</h3>
          </div>
          <div className="email-card-body">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Prize ID *
                </label>
                <input
                  type="text"
                  value={newPrize.id}
                  onChange={(e) => setNewPrize({...newPrize, id: e.target.value})}
                  className="input-gradient w-full"
                  placeholder="e.g., marathon_runner"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Prize Name *
                </label>
                <input
                  type="text"
                  value={newPrize.name}
                  onChange={(e) => setNewPrize({...newPrize, name: e.target.value})}
                  className="input-gradient w-full"
                  placeholder="e.g., Marathon Runner"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <input
                  type="text"
                  value={newPrize.description}
                  onChange={(e) => setNewPrize({...newPrize, description: e.target.value})}
                  className="input-gradient w-full"
                  placeholder="e.g., Completed hunt in over 2 hours"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Icon
                </label>
                <div className="flex flex-wrap gap-2">
                  {icons.map(icon => (
                    <button
                      key={icon}
                      onClick={() => setNewPrize({...newPrize, icon})}
                      className={`p-2 rounded border ${
                        newPrize.icon === icon ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
                      }`}
                    >
                      {icon}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rarity
                </label>
                <select
                  value={newPrize.rarity}
                  onChange={(e) => setNewPrize({...newPrize, rarity: e.target.value})}
                  className="input-gradient w-full"
                >
                  {rarities.map(rarity => (
                    <option key={rarity} value={rarity}>
                      {rarity.charAt(0).toUpperCase() + rarity.slice(1)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Condition
                </label>
                <select
                  value={newPrize.condition}
                  onChange={(e) => setNewPrize({...newPrize, condition: e.target.value})}
                  className="input-gradient w-full"
                >
                  {presetConditions.map(condition => (
                    <option key={condition.value} value={condition.value}>
                      {condition.label}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {presetConditions.find(c => c.value === newPrize.condition)?.description}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Threshold
                </label>
                <input
                  type="number"
                  value={newPrize.threshold}
                  onChange={(e) => setNewPrize({...newPrize, threshold: parseInt(e.target.value) || 1})}
                  className="input-gradient w-full"
                  min="1"
                />
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowAddForm(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAddPrize}
                className="btn-gradient"
              >
                <Save className="h-4 w-4 mr-2" />
                Add Prize
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Prizes List */}
      <div className="email-card">
        <div className="email-card-header">
          <h3 className="text-lg font-semibold">Current Prizes</h3>
        </div>
        <div className="email-card-body">
          <div className="space-y-4">
            {prizes.map((prize) => (
              <div key={prize.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                <div className="flex items-center space-x-4">
                  <span className="text-2xl">{prize.icon}</span>
                  <div>
                    <h4 className="font-medium text-gray-900">{prize.name}</h4>
                    <p className="text-sm text-gray-600">{prize.description}</p>
                    <div className="flex items-center space-x-2 mt-1">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getRarityColor(prize.rarity)}`}>
                        {prize.rarity}
                      </span>
                      <span className="text-xs text-gray-500">
                        {presetConditions.find(c => c.value === prize.condition)?.label}: {prize.threshold}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => togglePrizeEnabled(prize.id)}
                    className={`px-3 py-1 rounded-full text-xs font-medium ${
                      prize.enabled 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {prize.enabled ? 'Enabled' : 'Disabled'}
                  </button>
                  <button
                    onClick={() => handleEditPrize(prize)}
                    className="text-blue-600 hover:text-blue-900 p-1"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDeletePrize(prize.id)}
                    className="text-red-600 hover:text-red-900 p-1"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {editingPrize && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Edit Prize</h3>
                <button
                  onClick={() => setEditingPrize(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Prize Name
                  </label>
                  <input
                    type="text"
                    value={editingPrize.name}
                    onChange={(e) => setEditingPrize({...editingPrize, name: e.target.value})}
                    className="input-gradient w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <input
                    type="text"
                    value={editingPrize.description}
                    onChange={(e) => setEditingPrize({...editingPrize, description: e.target.value})}
                    className="input-gradient w-full"
                  />
                </div>
                <div className="flex space-x-4">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Rarity
                    </label>
                    <select
                      value={editingPrize.rarity}
                      onChange={(e) => setEditingPrize({...editingPrize, rarity: e.target.value})}
                      className="input-gradient w-full"
                    >
                      {rarities.map(rarity => (
                        <option key={rarity} value={rarity}>
                          {rarity.charAt(0).toUpperCase() + rarity.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Icon
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {icons.map(icon => (
                        <button
                          key={icon}
                          onClick={() => setEditingPrize({...editingPrize, icon})}
                          className={`p-2 rounded border ${
                            editingPrize.icon === icon ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
                          }`}
                        >
                          {icon}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setEditingPrize(null)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveEdit}
                  className="btn-gradient"
                >
                  <Save className="h-4 w-4 mr-2" />
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrizeManagement;
